import { Heart, Languages, Mail, MapPin, Play, ShieldCheck, Sparkles, User } from "lucide-react";
import { FOUNDER, TESTIMONIALS, FOUNDER_MEDIA } from "@/data/content";
import { useLang } from "@/i18n";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { SectionLabel } from "./ui";
import { Reveal } from "./Reveal";

const CARD_ICONS = [Languages, ShieldCheck, Heart];
const FACT_ICONS = [MapPin, Languages, Mail];

export const HumanTrust = () => {
  const { t } = useLang();
  const tr = t.trust;
  return (
    <section id="trust" data-testid="trust-section" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <Reveal className="max-w-2xl">
          <SectionLabel>{tr.label}</SectionLabel>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-navy-900 leading-tight">{tr.title}</h2>
          <p className="mt-4 text-base md:text-lg text-charcoal-700 leading-relaxed">{tr.sub}</p>
        </Reveal>

        <div className="mt-10 grid lg:grid-cols-12 gap-6 items-start">
          {/* Founder card */}
          <Reveal className="lg:col-span-5">
            <div className="relative rounded-[1.5rem] border border-navy-100 bg-[#FAFAFC] p-6 sm:p-8 overflow-hidden" data-testid="founder-card">
              <span className="absolute inset-x-0 top-0 h-1 brand-gradient" />
              <div className="flex items-center gap-5">
                {FOUNDER_MEDIA.photo ? (
                  <img src={FOUNDER_MEDIA.photo} alt={tr.founderName} className="h-20 w-20 rounded-2xl object-cover" loading="lazy" />
                ) : (
                  <span className="flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-2xl brand-gradient-deep text-white" title={tr.photoSoon} data-testid="founder-photo-placeholder">
                    <User size={28} aria-hidden />
                    <span className="mt-0.5 text-[9px] font-semibold opacity-80">{FOUNDER.initials}</span>
                  </span>
                )}
                <div>
                  <p className="font-display text-xl font-semibold text-navy-900">{tr.founderName}</p>
                  <p className="text-sm text-charcoal-700/70">{tr.founderRole}</p>
                </div>
              </div>
              <ul className="mt-6 space-y-2.5">
                {tr.facts.map((f, i) => {
                  const Icon = FACT_ICONS[i];
                  return (
                    <li key={f.label} className="flex items-center gap-3 text-sm text-charcoal-700">
                      <Icon size={16} className="text-gold-600 shrink-0" aria-hidden />
                      <span><span className="font-semibold text-navy-900">{f.label}:</span> {f.value}</span>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-5 text-xs text-charcoal-700/60">{tr.legal}</p>
            </div>
          </Reveal>

          {/* Founder video placeholder + trust cards */}
          <div className="lg:col-span-7">
            <Reveal delay={0.08}>
              <div className="rounded-[1.5rem] p-[3px] bg-gradient-to-br from-navy-200 via-gold-400 to-navy-100 shadow-xl">
                <div className="relative rounded-[calc(1.5rem-3px)] overflow-hidden bg-navy-900 aspect-video">
                  {FOUNDER_MEDIA.video ? (
                    <video
                      controls
                      preload="metadata"
                      poster={FOUNDER_MEDIA.videoPoster || undefined}
                      className="h-full w-full object-cover"
                      data-testid="founder-video-player"
                      onPlay={() => trackEvent(EVENTS.FOUNDER_VIDEO_PLAY)}
                    >
                      <source src={FOUNDER_MEDIA.video} type="video/mp4" />
                      {FOUNDER_MEDIA.videoCaptions && <track kind="captions" src={FOUNDER_MEDIA.videoCaptions} srcLang="en" label="English" />}
                    </video>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center" data-testid="founder-video-placeholder">
                      <div className="absolute inset-0 opacity-35" style={{ background: "radial-gradient(60% 80% at 50% 0%, rgba(212,175,55,0.3), transparent 70%)" }} aria-hidden />
                      <span className="relative flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full border-2 border-gold-500/40 bg-white/5 text-gold-500/60 cursor-not-allowed" aria-disabled="true" aria-label={tr.videoTitle}>
                        <Play size={22} aria-hidden className="ml-0.5" />
                      </span>
                      <p className="relative mt-3 font-display text-lg sm:text-xl font-semibold text-white">{tr.videoTitle}</p>
                      <p className="relative mt-1.5 max-w-md text-xs sm:text-sm text-white/70">{tr.videoDesc}</p>
                    </div>
                  )}
                </div>
              </div>
            </Reveal>

            <div className="mt-5 grid sm:grid-cols-3 gap-3.5">
              {tr.cards.map((c, i) => {
                const Icon = CARD_ICONS[i];
                return (
                  <Reveal key={c.title} delay={0.1 + i * 0.05}>
                    <div className="h-full rounded-2xl border border-navy-100 bg-white p-4 shadow-[0_2px_10px_rgba(6,16,32,0.04)]">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gold-500/15 text-gold-600">
                        <Icon size={18} aria-hidden />
                      </span>
                      <p className="mt-2.5 font-semibold text-navy-900 text-sm">{c.title}</p>
                      <p className="mt-1 text-xs text-charcoal-700/80 leading-relaxed">{c.desc}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>

            {TESTIMONIALS.length === 0 && (
              <Reveal delay={0.2}>
                <p className="mt-5 flex items-center gap-2 text-sm text-charcoal-700/70">
                  <Sparkles size={16} className="text-gold-600" aria-hidden /> {tr.firstStory}
                </p>
              </Reveal>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
